import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSim } from "@/context/SimContext";
import { DOG_BREEDS, CAT_BREEDS } from "@/data/breeds";
import { CASE_SCENARIOS } from "@/data/cases";
import { useCaseList } from "@/context/ContentContext";
import StepNav from "@/components/StepNav";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dog, Cat, ChevronRight, Stethoscope, BookOpen, Sparkles } from "lucide-react";
import { toast } from "sonner";

const ASA_CLASSES = [
  { v: "I", label: "I — Healthy", color: "text-emerald-700" },
  { v: "II", label: "II — Mild systemic disease", color: "text-lime-700" },
  { v: "III", label: "III — Severe systemic disease", color: "text-amber-700" },
  { v: "IV", label: "IV — Constant threat to life", color: "text-orange-700" },
  { v: "V", label: "V — Moribund", color: "text-red-700" },
  { v: "E", label: "E — Emergency modifier", color: "text-slate-600" },
];

export default function PatientIntake() {
  const navigate = useNavigate();
  const { patient, setPatient, setPlan } = useSim();
  const cases = useCaseList("anaesthesia", CASE_SCENARIOS);

  const [form, setForm] = useState(
    patient || {
      species: "dog",
      name: "",
      breed: "",
      sex: "male_neutered",
      ageYears: "",
      ageMonths: "",
      weightKg: "",
      asa: "II",
      procedure: "",
      bcs: "5",
      notes: "",
      bloods: "",
    }
  );

  const breeds = useMemo(
    () => (form.species === "cat" ? CAT_BREEDS : DOG_BREEDS),
    [form.species]
  );

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadCase = (c) => {
    setForm({ ...c.patient });
    if (c.suggestedPlan) setPlan(c.suggestedPlan);
    toast.success(`Loaded case: ${c.title}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const w = parseFloat(form.weightKg);
    if (!form.name.trim()) return toast.error("Patient name required");
    if (!form.breed) return toast.error("Please select a breed");
    if (isNaN(w) || w <= 0) return toast.error("Enter a valid weight (kg)");
    if (form.species === "cat" && w > 15)
      toast.warning("Unusually high weight for a cat — please confirm.");
    if (form.species === "dog" && w > 90)
      toast.warning("Very large dog — please confirm weight.");

    setPatient({ ...form, weightKg: w });
    navigate("/plan");
  };

  return (
    <div className="min-h-screen bg-[#F4F7F9]">
      <StepNav current={1} />
      <main className="max-w-4xl mx-auto px-6 py-10 fade-up">
        <header className="mb-8">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0047AB] font-semibold mb-2">
            <Stethoscope className="w-3.5 h-3.5" />
            <span>Pre-anaesthetic assessment</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
            Patient intake
          </h1>
          <p className="text-base text-slate-600 mt-2 max-w-2xl">
            Record patient demographics and physical status. Drug doses on the
            next screen are calculated from the weight you enter here.
          </p>
        </header>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Pre-built case scenarios */}
          <Card className="border-slate-200" data-testid="cases-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-[#0047AB]" />
                Case library
                <span className="ml-auto text-[10px] text-slate-500 font-normal normal-case tracking-normal">
                  Click any preset to auto-fill the form
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {cases.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => loadCase(c)}
                    data-testid={`case-${c.id}-btn`}
                    className="text-left border-2 border-slate-200 hover:border-[#0047AB] hover:bg-[#0047AB]/5 rounded-md p-3 transition-all group"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="w-3.5 h-3.5 text-[#0047AB]" />
                      <div className="font-semibold text-slate-900 text-sm">
                        {c.title}
                      </div>
                      <span
                        className={`ml-auto text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${
                          c.difficulty === "Beginner"
                            ? "bg-emerald-50 text-emerald-700"
                            : c.difficulty === "Intermediate"
                            ? "bg-amber-50 text-amber-700"
                            : "bg-red-50 text-red-700"
                        }`}
                      >
                        {c.difficulty}
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 leading-snug">
                      {c.blurb}
                    </p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Species selector */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-slate-900 uppercase tracking-wider">
                Species
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { v: "dog", label: "Canine", Icon: Dog },
                  { v: "cat", label: "Feline", Icon: Cat },
                ].map(({ v, label, Icon }) => {
                  const active = form.species === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => {
                        update("species", v);
                        update("breed", "");
                      }}
                      data-testid={`species-${v}-btn`}
                      className={`flex items-center gap-3 px-5 py-4 rounded-md border-2 transition-all text-left ${
                        active
                          ? "border-[#0047AB] bg-[#0047AB]/5"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                    >
                      <Icon
                        className={`w-7 h-7 ${
                          active ? "text-[#0047AB]" : "text-slate-400"
                        }`}
                        strokeWidth={1.8}
                      />
                      <div>
                        <div className="font-semibold text-slate-900">
                          {label}
                        </div>
                        <div className="text-xs text-slate-500">
                          {v === "dog" ? "Canis familiaris" : "Felis catus"}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Demographics */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-slate-900 uppercase tracking-wider">
                Demographics
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="name">Patient name</Label>
                <Input
                  id="name"
                  data-testid="patient-name-input"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="e.g. Bella"
                />
              </div>

              <div className="space-y-2">
                <Label>Breed</Label>
                <Select
                  value={form.breed}
                  onValueChange={(v) => update("breed", v)}
                >
                  <SelectTrigger data-testid="patient-breed-select">
                    <SelectValue placeholder="Select breed" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {breeds.map((b) => (
                      <SelectItem key={b} value={b}>
                        {b}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Sex</Label>
                <Select
                  value={form.sex}
                  onValueChange={(v) => update("sex", v)}
                >
                  <SelectTrigger data-testid="patient-sex-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male_entire">Male (entire)</SelectItem>
                    <SelectItem value="male_neutered">Male (neutered)</SelectItem>
                    <SelectItem value="female_entire">Female (entire)</SelectItem>
                    <SelectItem value="female_spayed">Female (spayed)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Age</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="30"
                    data-testid="patient-age-years-input"
                    value={form.ageYears}
                    onChange={(e) => update("ageYears", e.target.value)}
                    placeholder="Years"
                  />
                  <Input
                    type="number"
                    min="0"
                    max="11"
                    data-testid="patient-age-months-input"
                    value={form.ageMonths}
                    onChange={(e) => update("ageMonths", e.target.value)}
                    placeholder="Months"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="weight">
                  Weight <span className="text-[#0047AB]">(kg)</span>
                </Label>
                <Input
                  id="weight"
                  type="number"
                  step="0.01"
                  min="0.1"
                  data-testid="patient-weight-input"
                  value={form.weightKg}
                  onChange={(e) => update("weightKg", e.target.value)}
                  placeholder="e.g. 24.5"
                  className="font-mono-clinical text-base"
                />
              </div>

              <div className="space-y-2">
                <Label>Body condition score (1–9)</Label>
                <Select
                  value={form.bcs}
                  onValueChange={(v) => update("bcs", v)}
                >
                  <SelectTrigger data-testid="patient-bcs-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n}/9{" "}
                        {n <= 3
                          ? "(Underweight)"
                          : n <= 5
                          ? "(Ideal)"
                          : n <= 7
                          ? "(Overweight)"
                          : "(Obese)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* ASA + Procedure */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-slate-900 uppercase tracking-wider">
                Physical status & procedure
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label className="mb-3 block">ASA Classification</Label>
                <RadioGroup
                  value={form.asa}
                  onValueChange={(v) => update("asa", v)}
                  className="grid grid-cols-2 md:grid-cols-3 gap-2"
                  data-testid="patient-asa-radio"
                >
                  {ASA_CLASSES.map((a) => (
                    <label
                      key={a.v}
                      htmlFor={`asa-${a.v}`}
                      className={`flex items-center gap-3 border-2 rounded-md px-3 py-2 cursor-pointer transition-all ${
                        form.asa === a.v
                          ? "border-[#0047AB] bg-[#0047AB]/5"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <RadioGroupItem
                        value={a.v}
                        id={`asa-${a.v}`}
                        data-testid={`asa-${a.v}-radio`}
                      />
                      <span className={`text-xs font-medium ${a.color}`}>
                        {a.label}
                      </span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label htmlFor="procedure">Procedure / Indication</Label>
                <Input
                  id="procedure"
                  data-testid="patient-procedure-input"
                  value={form.procedure}
                  onChange={(e) => update("procedure", e.target.value)}
                  placeholder="e.g. Ovariohysterectomy, dental scale & polish"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Clinical notes</Label>
                <Textarea
                  id="notes"
                  data-testid="patient-notes-textarea"
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  placeholder="Relevant history, current medications, recent bloodwork..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bloods">
                  Pre-anaesthetic findings &amp; bloodwork
                </Label>
                <Textarea
                  id="bloods"
                  data-testid="patient-bloods-textarea"
                  value={form.bloods}
                  onChange={(e) => update("bloods", e.target.value)}
                  placeholder="e.g. PCV 42%, TP 68 g/L, urea/creatinine WNL. Mild grade II/VI murmur. Otherwise NAD."
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end pt-2">
            <Button
              type="submit"
              size="lg"
              data-testid="continue-to-plan-btn"
              className="bg-[#0047AB] hover:bg-[#003380] text-white px-6"
            >
              Continue to anaesthetic plan
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
